/**
 * Shown when the credit balance can no longer cover any AI action.
 *
 * The point is to remove guesswork: without it, an empty balance looks like a
 * broken app — buttons do nothing, automations quietly stop. So it names what
 * is paused, what still works (sending is free), and offers one way out.
 *
 * The support address is intentionally not rendered as text; the button opens
 * a prefilled mail instead.
 */

import React, { useEffect } from 'react';
import './CreditsExhaustedModal.css';

const SUPPORT_ADDRESS = 'julian@bonusclub.io';

const PAUSED = [
  { icon: 'fa-wand-magic-sparkles', label: 'Email generation in Normal, Bulk and Auto-Send mode' },
  { icon: 'fa-clone', label: 'CopyTwin / Voice Clone emails' },
  { icon: 'fa-magnifying-glass-chart', label: 'Research: web research, decision-maker lookup, potential analyzer, contact cleaning' },
  { icon: 'fa-image', label: 'Image Lab renders' },
  // Worded from what the backend actually does: outreach jobs flip to paused
  // with a reason and can be restarted, while a flow step parks the mail in
  // Drafts. Promising that everything "picks up again" would be wrong.
  { icon: 'fa-robot', label: 'Automated outreach jobs — they pause with a note and can be restarted after a top-up' },
  { icon: 'fa-folder-open', label: 'Flow steps — the email is parked in Drafts instead of being sent' },
];

const fmt = (n) => (Number(n) || 0).toLocaleString('en-US', { maximumFractionDigits: 2 });

/**
 * Slim always-on strip under the header. The modal can be dismissed, and there
 * is no credit counter in the dashboard chrome — without this, a user who
 * clicked it away has no standing reminder for why generation stays dead.
 */
export function CreditsExhaustedBanner({ balance = 0, onOpen }) {
  return (
    <div className="credits-out-bar" role="status">
      <i className="fas fa-triangle-exclamation" aria-hidden="true"></i>
      <span className="credits-out-bar__text">
        <strong>No credits left.</strong> AI generation, research and automated jobs are paused.
        Sending already generated emails still works.
      </span>
      <span className="credits-out-bar__balance">{fmt(balance)} credits</span>
      <button type="button" className="credits-out-bar__cta" onClick={onOpen}>
        Get more credits
      </button>
    </div>
  );
}

export default function CreditsExhaustedModal({
  balance = 0,
  requiredCost = null,
  sharedPool = false,
  onClose,
}) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const requestMoreCredits = () => {
    const subject = 'Credit top-up request';
    const body = [
      'Hi Julian,',
      '',
      'my credit balance is empty and I would like to top up.',
      '',
      `Current balance: ${fmt(balance)} credits`,
      '',
      'Thanks!',
    ].join('\n');
    window.location.href =
      `mailto:${SUPPORT_ADDRESS}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <div
      className="credits-out__overlay"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className="credits-out" role="dialog" aria-modal="true" aria-labelledby="credits-out-title">
        <button type="button" className="credits-out__close" onClick={onClose} aria-label="Close">
          <i className="fas fa-xmark" aria-hidden="true"></i>
        </button>

        <div className="credits-out__head">
          <span className="credits-out__icon" aria-hidden="true">
            <i className="fas fa-triangle-exclamation"></i>
          </span>
          <h3 className="credits-out__title" id="credits-out-title">
            You&apos;re out of credits
          </h3>
          <p className="credits-out__lead">
            {/* "Everything that costs credits", not "every AI feature": a few AI
                helpers are not metered and keep working, so the broader claim
                would be wrong. */}
            Everything that costs credits is paused until the balance is topped up.
            Nothing is broken — and none of your work is lost.
          </p>
          <span className="credits-out__badge">
            {fmt(balance)} credits left
            {requiredCost ? <> · this action needs {fmt(requiredCost)}</> : null}
          </span>
        </div>

        <div className="credits-out__body">
          <p className="credits-out__section-title">What is paused right now</p>
          <ul className="credits-out__list">
            {PAUSED.map((item) => (
              <li key={item.label}>
                <i className={`fas ${item.icon}`} aria-hidden="true"></i>
                <span>{item.label}</span>
              </li>
            ))}
          </ul>

          <p className="credits-out__still-works">
            <i className="fas fa-circle-check" aria-hidden="true"></i>
            <span>
              <strong>Sending still works.</strong> Emails you already generated can be sent as
              usual — delivery never costs credits. Your inbox, leads and settings are untouched.
            </span>
          </p>

          {sharedPool && (
            <p className="credits-out__note">
              This workspace draws from the account owner&apos;s shared credit pool, so the top-up
              has to happen on the main account.
            </p>
          )}
        </div>

        <div className="credits-out__actions">
          <button type="button" className="credits-out__secondary" onClick={onClose}>
            Got it
          </button>
          <button type="button" className="credits-out__primary" onClick={requestMoreCredits}>
            <i className="fas fa-bolt" aria-hidden="true"></i>
            Get more credits
          </button>
        </div>
      </div>
    </div>
  );
}
